/**
 * clasificadores/contribucionNeta.js — Contribución Neta por Orden
 * (módulo Rentabilidad de Mercado Libre).
 *
 * Función pura: recibe datos crudos de Firestore, devuelve secciones
 * clasificadas con publicaciones accionables.
 *
 * ═══ Rework de acción única (12-jul-2026, decisión Mariano) ═══
 * El módulo tiene UNA palanca propia: el PRECIO. Decidir si conviene apagar
 * la publicidad es del dashboard Publicidad; decidir si conviene bajar una
 * promo es de Central de Promociones — esos motores son más finos y evitan
 * el dedupe cross-dashboard. Contribución Neta solo mira: "dadas las
 * condiciones en que se vendió (con su ad y su descuento), ¿a qué precio hay
 * que venderlo para ganar el mínimo aceptable?".
 *
 * DOS tareas (tipos de catálogo distintos → cuota y dedupe independientes):
 *   📈 subir_precio   (alta) → la pub vendió A PÉRDIDA (margen efectivo < 0).
 *   📊 mejorar_margen (baja) → NO a pérdida pero por debajo del margen mínimo
 *                              aceptado (0 ≤ margen efectivo < corte).
 *
 * Margen mínimo = `corte` de la ficha del producto ("Rentabilidad mínima
 * aceptada"), resuelto por la cascada ADR 032 producto → categoría → marca →
 * proveedor → empresa (construirInfoRentabilidadPorItem). Si NINGÚN nivel lo
 * tiene cargado → el ítem NO se toma (cero tareas). Si además está a pérdida,
 * se DECLARA (excluidos.perdidaSinCorte) para que el operador cargue el
 * mínimo — un cero mudo escondería una pérdida real.
 *
 * Precio sugerido (en las dos, misma cuenta): el precio publicado donde la
 * cascada real da el `corte`, MANTENIENDO las palancas — el descuento % y el
 * costo de ad por unidad observados quedan puestos. Se resuelve por bisección
 * sobre la misma fórmula pura (calcularRentabilidadLinea): los gastos que son
 * % del precio (comisión, IVA, IIBB) escalan; los fijos por unidad (costo,
 * envío, ad) quedan. Si el mínimo es inalcanzable subiendo el precio (la
 * asíntota queda por debajo del corte) → precioSugerido null (pérdida
 * estructural de costo, no de precio). El envío/u sale sólo de las líneas que
 * lo informan; si ninguna lo informa → precioSugerido null con
 * `_envioNoInformado` (sin el dato no se sugiere: un 0 lo bajaría).
 * Ojo: acá `_envioNoInformado` es POR PUBLICACIÓN (ninguna de sus líneas lo
 * informa); en las filas del dashboard el mismo nombre es POR LÍNEA.
 *
 * ⭐ PARIDAD: la renta por orden se hidrata con `hidratarLineasOrden`, LA MISMA
 * función que usa el `rows` del dashboard. Vista y agente comen lo mismo.
 * ADR 042: solo cuentan las líneas con costo cargado (sin costo, la renta es
 * ficción — no se fabrica accionable con ella).
 *
 * Output:
 *   {
 *     secciones: { subir_precio: array, mejorar_margen: array },
 *     analisis:  { pubsAfectadas, perdidaTotal, ventanaDias },
 *
 * `analisis.ventanaDias` es la ventana que CUBRE este análisis, y puede ser
 * `null`: es la ventana con la que el llamador cargó `ventas30d`, no un umbral
 * configurado ni un default de este archivo. Son los DÍAS NOMINALES que se
 * pidieron — el rango real abarca un día calendario más, porque abre en la
 * medianoche de hoy−N y cierra al final de hoy. Sin ventana declarada el
 * análisis dice que no la sabe; inventar un 30 acá es el defecto que el report
 * djynjlXICuUF0Yl803M8 vino a cerrar.
 *     excluidos: { perdidaSinCorte: array },
 *   }
 */

import {
  hidratarLineasOrden,
  construirProductoInfoPorItem,
  construirCostoAdPorItem,
  construirEnvioGratisPorItem,
} from '../rentabilidad/cascadaOrden'
import { calcularRentabilidadLinea, derivarCostosNoInformados, COSTOS_DECLARABLES } from '../rentabilidad'
import { construirInfoRentabilidadPorItem } from './gatePromoRentabilidad'
import { ventanaDeVentas } from './ventanaDeVentas'

// ─── Helpers ─────────────────────────────────────────────────────────────
function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0 }

function tienePromoActivaHoy(pub) {
  if (!pub) return false
  const deals = Array.isArray(pub.dealIds) ? pub.dealIds : []
  if (deals.length > 0) return true
  const op = num(pub.originalPrice)
  const px = num(pub.price)
  return op > 0 && px > 0 && op > px
}

function fechaMsDe(v) {
  const dc = v?.date_created
  if (!dc) return 0
  if (typeof dc === 'string') return Date.parse(dc) || 0
  if (dc?.toDate) return dc.toDate().getTime()
  if (dc instanceof Date) return dc.getTime()
  return 0
}

/**
 * Precio publicado por unidad que hace que la cascada dé `objetivoMargen`,
 * manteniendo el descuento % y el costo de ad por unidad observados. Bisección
 * sobre la fórmula pura compartida (calcularRentabilidadLinea). `objetivoMargen`
 * puede ser 0 (equilibrio: dejar de perder) o el corte de la ficha (ganar el
 * mínimo). Devuelve null si el objetivo es inalcanzable subiendo el precio (la
 * asíntota del margen queda por debajo).
 */
function calcularPrecioParaObjetivo({
  objetivoMargen, precioActual, descuentoRatio, comisionRatio, envioUnit,
  costoUnit, adUnit, ivaPct, impuestos,
}) {
  if (objetivoMargen == null || objetivoMargen >= 1 || !(precioActual > 0) || !(costoUnit > 0)) return null
  const flags = impuestos?.flags || {
    discriminaIVA: true, pagaIIBB: true, pagaImpCredDebito: true, aplicaGananciasEstimado: true,
  }
  const dRatio = (descuentoRatio >= 0 && descuentoRatio < 1) ? descuentoRatio : 0
  const iibbRate = num(impuestos?.iibb)
  const impCredDebitoRate = num(impuestos?.impuestoCredDebito)
  const factorGanancias = num(impuestos?.factorGanancias)

  function margenAt(P) {
    const subtotal = P * (1 - dRatio)   // precio de venta efectivo (post-descuento)
    if (subtotal <= 0) return -Infinity
    const r = calcularRentabilidadLinea({
      tieneLink: true, tieneCosto: true,
      subtotal, qty: 1, costoUnit,
      comisionMLTotal: comisionRatio * subtotal,
      envioSellerLinea: envioUnit,
      billing: null, adUnit,
      ivaPct, flags, iibbRate, impCredDebitoRate, factorGanancias,
    })
    return r.margenPct == null ? -Infinity : r.margenPct
  }

  // ¿Alcanzable? Expandir el techo hasta que el margen llegue al objetivo.
  let hi = precioActual
  let guard = 0
  while (margenAt(hi) < objetivoMargen && guard < 30) { hi *= 1.4; guard++ }
  if (margenAt(hi) < objetivoMargen) return null   // asíntota < objetivo: no se llega subiendo precio

  // Piso: un precio donde el margen quede por debajo del objetivo.
  let lo = precioActual
  guard = 0
  while (margenAt(lo) >= objetivoMargen && guard < 40) { lo *= 0.6; guard++ }
  if (margenAt(lo) >= objetivoMargen) return lo   // hasta un precio ínfimo alcanza (raro) — devolvemos ese piso

  for (let i = 0; i < 64; i++) {
    const mid = (lo + hi) / 2
    if (margenAt(mid) < objetivoMargen) lo = mid
    else hi = mid
  }
  return hi
}

// ─── Función principal ──────────────────────────────────────────────────
export function clasificarContribucionNeta({
  ventas30d = [],
  publicaciones = [],
  adsItems = [],
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,   // empresas/{id}/config/rentabilidad — corte empresa (cascada ADR 032)
  impuestosEmpresa = null,       // config fiscal mergeada (loader del agente / dashboard)
  config = {},
  // La ventana con la que el llamador cargó `ventas30d`. La publica el loader
  // del agente en `datos.ventanaVentasDias` (agentes.js) y la pasa el
  // dashboard desde su selector de período. El destructuring de esta firma es
  // CERRADO: un insumo que no esté nombrado acá se descarta en silencio (ver
  // el test de caracterización de `productoBitacora`), así que el nombre tiene
  // que coincidir con el del campo que viaja.
  //
  // `umbrales` ya no se lee: era la SEGUNDA fuente de la ventana y es el
  // defecto que esto cierra. Un umbral que el operador configura no describe
  // el rango con el que entraron los datos, así que no puede ni ganar ni
  // actuar de fallback — con él de fallback la etiqueta volvía a poder mentir.
  ventanaVentasDias = null,
}) {
  // Número o `null`, nunca otra cosa (ver `ventanaDeVentas`).
  const ventanaDias = ventanaDeVentas(ventanaVentasDias)
  const impuestos = impuestosEmpresa || {}

  // ─── Índices compartidos con el dashboard (cascadaOrden.js) ───────────
  const productoInfoPorItem = construirProductoInfoPorItem({ productos, catalogoLinks, categorias })
  const costoAdPorItem = construirCostoAdPorItem(adsItems)
  // Envío-gratis-seller por ítem (reporte ymCwUxBd, opción A) — MISMA señal que
  // usa el dashboard, para que el envío se reparta igual en ambos (paridad).
  const envioGratisPorItem = construirEnvioGratisPorItem(publicaciones)

  // ─── Corte de rentabilidad mínima por ítem (cascada ADR 032) ──────────
  // producto → categoría → marca → proveedor → empresa. Sin valor válido en
  // ningún nivel → corte null → el ítem NO se toma.
  const infoRentPorItem = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })

  // ─── pub por itemId + ad inscripto hoy ────────────────────────────────
  const pubPorItem = new Map()
  for (const p of publicaciones) pubPorItem.set(String(p.itemId ?? p.id), p)
  const itemsConAdHoy = new Set()
  for (const ad of adsItems) {
    const itemId = ad.item_id || (ad.id ? String(ad.id).split('_').pop() : null)
    if (itemId && costoAdPorItem.has(String(itemId))) itemsConAdHoy.add(String(itemId))
  }

  // ─── Acumular economía real por ítem (solo líneas con costo — ADR 042) ─
  // Un acumulador por publicación: sumas para el margen efectivo y los ratios
  // (comisión %, envío/u, descuento %) que alimentan la bisección del precio.
  const acc = new Map()
  for (const v of ventas30d) {
    const hid = hidratarLineasOrden({ venta: v, productoInfoPorItem, costoAdPorItem, impuestosEmpresa: impuestos, envioGratisPorItem })
    if (hid.subtotalOrden <= 0) continue
    const fechaMs = fechaMsDe(v)
    for (const l of hid.lineas) {
      if (!l.itemId || !l.tieneCosto) continue   // ADR 042: sin costo no aporta renta
      let a = acc.get(l.itemId)
      if (!a) {
        a = {
          itemId: l.itemId,
          sumaSubtotal: 0, sumaRenta: 0, sumaComision: 0,
          sumaEnvio: 0, sumaQtyEnvio: 0, sumaDescuento: 0, sumaPublicidad: 0,
          costoUnit: l.costoUnit, adUnit: l.adUnit, ivaPct: l.ivaPct,
          ordenes: [],
        }
        acc.set(l.itemId, a)
      }
      const rentaLinea = num(l.rentResult.rentaNeta)
      a.sumaSubtotal += l.subtotal
      a.sumaRenta += rentaLinea
      a.sumaComision += l.comisionMLFinal
      // El envío/u del precio sugerido sale sólo de las líneas que lo
      // informan (suma Y cantidad): un `no-informada` entra 0 y ese 0 no es
      // «sin costo» (mismo criterio que estructuraCostosML).
      if (!derivarCostosNoInformados(l).includes(COSTOS_DECLARABLES.envio)) {
        a.sumaEnvio += l.envioSeller
        a.sumaQtyEnvio += l.qty
      }
      a.sumaDescuento += l.descuento
      a.sumaPublicidad += l.publicidad
      a.costoUnit = l.costoUnit || a.costoUnit
      a.adUnit = l.adUnit || a.adUnit
      a.ivaPct = l.ivaPct
      a.ordenes.push({ orderId: v.id, rentaNeta: rentaLinea, subtotal: l.subtotal, qty: l.qty, fechaMs })
    }
  }

  // ─── Decisión por publicación ─────────────────────────────────────────
  // REGLA (12-jul-2026, decisión Mariano):
  //   - A PÉRDIDA (margen < 0) → SIEMPRE subir_precio. La pérdida es objetiva,
  //     no depende del corte. Si hay corte, el precio apunta a ganarlo; si NO
  //     hay corte, apunta al EQUILIBRIO (dejar de perder) y se avisa que
  //     cargando el mínimo apunta a ganancia.
  //   - NO a pérdida pero bajo el corte → mejorar_margen. Este SÍ necesita el
  //     corte (sin mínimo no se puede definir "bajo el mínimo").
  const subir_precio = []
  const mejorar_margen = []
  let perdidaTotalRojo = 0
  let sinCorteEnPerdida = 0

  for (const [itemId, a] of acc.entries()) {
    if (a.sumaSubtotal <= 0) continue
    const pub = pubPorItem.get(itemId)
    if (!pub) continue

    const margenEfectivo = a.sumaRenta / a.sumaSubtotal
    const corte = infoRentPorItem.get(itemId)?.corte ?? null
    const precioActual = num(pub.price)
    const aPerdida = a.sumaRenta < 0

    // mejorar_margen (no a pérdida) requiere corte; si no hay o ya está sana, sale.
    if (!aPerdida && (corte == null || margenEfectivo >= corte)) continue

    // Objetivo del precio: el corte si está cargado; si no (solo posible a
    // pérdida) → equilibrio (0 = dejar de perder).
    const objetivoMargen = corte != null ? corte : 0
    const objetivoEquilibrio = corte == null
    const precioLista = a.sumaSubtotal + a.sumaDescuento
    const descuentoRatio = precioLista > 0 ? a.sumaDescuento / precioLista : 0
    const comisionRatio = a.sumaSubtotal > 0 ? a.sumaComision / a.sumaSubtotal : 0
    // Sin ninguna línea que informe el envío no se sugiere precio: con 0 el
    // sugerido saldría más bajo de lo necesario. Se declara aparte de «no
    // alcanzable», que es otra cosa (el costo, no el dato).
    const envioNoInformado = a.sumaQtyEnvio === 0
    const precioSugerido = envioNoInformado ? null : calcularPrecioParaObjetivo({
      objetivoMargen, precioActual, descuentoRatio, comisionRatio,
      envioUnit: a.sumaEnvio / a.sumaQtyEnvio,
      costoUnit: a.costoUnit, adUnit: a.adUnit, ivaPct: a.ivaPct, impuestos,
    })

    const row = {
      id: itemId,
      _itemId: itemId,
      _titulo: pub.title || itemId,
      _permalink: pub.permalink || null,
      _productoId: infoRentPorItem.get(itemId)?.productoId || null,
      _precio: precioActual,
      _costoUnit: a.costoUnit,
      _corte: corte,
      _objetivoMargen: objetivoMargen,
      _objetivoEquilibrio: objetivoEquilibrio,
      _margenActual: margenEfectivo,
      _precioSugerido: precioSugerido,
      _deltaPrecio: (precioSugerido != null && precioActual > 0) ? precioSugerido - precioActual : null,
      _noAlcanzablePorPrecio: !envioNoInformado && precioSugerido == null,
      _envioNoInformado: envioNoInformado,
      _tieneAdHoy: itemsConAdHoy.has(itemId),
      _tienePromoHoy: tienePromoActivaHoy(pub),
      _ordenesCount: a.ordenes.length,
      _ordenes: a.ordenes,
      _perdidaTotal: aPerdida ? Math.abs(a.sumaRenta) : 0,
      _motivo: aPerdida ? 'perdida' : 'bajo_minimo',
    }

    if (aPerdida) {
      perdidaTotalRojo += Math.abs(a.sumaRenta)
      if (objetivoEquilibrio) sinCorteEnPerdida++
      subir_precio.push(row)
    } else {
      mejorar_margen.push(row)
    }
  }

  // Orden: mayor pérdida / mayor brecha al corte primero.
  subir_precio.sort((x, y) => (y._perdidaTotal || 0) - (x._perdidaTotal || 0))
  mejorar_margen.sort((x, y) => (x._margenActual ?? 0) - (y._margenActual ?? 0))

  return {
    secciones: {
      subir_precio,
      mejorar_margen,
    },
    analisis: {
      pubsAfectadas: subir_precio.length + mejorar_margen.length,
      pubsPerdida: subir_precio.length,
      pubsBajoMinimo: mejorar_margen.length,
      perdidaTotal: perdidaTotalRojo,
      sinCorteEnPerdida,   // a pérdida cuyo precio apunta a equilibrio (sin mínimo cargado)
      ventanaDias,
    },
    excluidos: {
      perdidaSinCorte: [],   // ya no se excluye a pérdida — quedó por compat de shape
    },
  }
}

/**
 * Recurso de la tarea para una publicación clasificada — LA evidencia que se
 * persiste, venga del agente, del tab Tareas o de la selección de órdenes del
 * dashboard (report HXwwsN2cVVhPwP7A3k6V: cada escritor armaba la suya y la del
 * agente quedaba sin las órdenes que justifican la tarea). Un solo shape, en
 * el lado puro del clasificador, para que "vista y agente comen lo mismo"
 * valga también para lo que se guarda. Sin `undefined`: Firestore lo rechaza.
 */
export function recursoContribucionNeta(item) {
  return {
    itemId: item._itemId,
    titulo: item._titulo,
    permalink: item._permalink || null,
    productoId: item._productoId || null,
    rentabilidad: {
      precio: item._precio ?? null,
      costoUnit: item._costoUnit ?? null,
      margenActual: item._margenActual ?? null,
      corteMinimo: item._corte ?? null,
      precioSugerido: item._precioSugerido ?? null,
      deltaPrecio: item._deltaPrecio ?? null,
      noAlcanzablePorPrecio: !!item._noAlcanzablePorPrecio,
      envioNoInformado: !!item._envioNoInformado,
    },
    contribucion: {
      motivo: item._motivo || null,
      ordenesCount: item._ordenesCount ?? null,
      perdidaTotal: item._perdidaTotal ?? null,
      ordenes: Array.isArray(item._ordenes) ? item._ordenes : [],
      tieneAdHoy: item._tieneAdHoy ?? null,
      tienePromoHoy: item._tienePromoHoy ?? null,
    },
  }
}
